import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";

/**
 * AdminLogin — a distinct, minimal admin-only entry point. No Google button,
 * no customer signup / forgot links, no marketing copy. Reachable only by
 * typing /admin/login directly (never linked anywhere on the public site).
 * Customer accounts that log in here are bounced to Home.
 */
export default function AdminLogin() {
  const { user, login } = useApp();
  const nav = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // Already-logged-in admins go straight to the studio.
  useEffect(() => {
    if (user?.is_admin) nav("/admin", { replace: true });
    else if (user) nav("/", { replace: true }); // customer wandered here — send home
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(identifier, password);
      if (!u?.is_admin) {
        toast.error("This portal is for administrators only.");
        nav("/", { replace: true });
        return;
      }
      nav("/admin", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || "Invalid credentials";
      toast.error(detail, { duration: status === 429 ? 8000 : 4000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] grid place-items-center px-6 py-16" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center text-center">
          <Logo variant="monogram" className="h-14 w-14 dark:invert" loading="eager" />
          <div className="mt-6 inline-flex items-center gap-2 border px-3 py-1 label-xs">
            <ShieldCheck className="h-3.5 w-3.5" />
            Admin Portal
          </div>
          <h1 className="mt-6 text-4xl font-serif tracking-tighter">Admin Sign In</h1>
          <p className="mt-3 text-sm opacity-60">
            Restricted access. Store administrators only.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-5">
          <div>
            <Label className="label-xs">Username</Label>
            <Input
              type="text"
              autoComplete="username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              className="rounded-none mt-2"
              data-testid="admin-login-username"
            />
          </div>
          <div>
            <Label className="label-xs">Password</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-none mt-2"
              data-testid="admin-login-password"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-none label-xs"
            data-testid="admin-login-submit"
          >
            {busy ? "Signing in…" : "Sign In to Admin"}
          </Button>
        </form>

        <p className="mt-8 text-[11px] opacity-50 leading-relaxed text-center">
          Protected — 5 attempts, 15-minute cooldown.
        </p>
      </div>
    </div>
  );
}
